import React, { useState } from 'react';
import { sendPhoneVerificationCode, verifyPhoneCode } from '../utils/api';
import { useSession } from '../contexts/SessionContext';
import './PhoneVerification.css';

const PhoneVerification = () => {
  const { sessionId, personalInfo, setPhoneVerified, updateSessionData } = useSession();
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  const startResendTimer = () => {
    setResendTimer(60); // 60 seconds cooldown
    const interval = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    setLoading(true);
    setError('');

    // CIN-6961: flush the pending session PATCH before asking for a code. The phone
    // number reaches the server through the 500ms-debounced updateSessionData effect
    // in SessionContext, but this button mounts the instant the 10th digit is typed,
    // so a fast customer can click while the server still holds the previous value -
    // NULL for any partial number - and send-validation answers 400 for a perfectly
    // valid number. updateSessionData already swallows its own API errors; this guard
    // only exists so nothing it may ever throw can block the send.
    try {
      await updateSessionData();
    } catch (flushError) {
      console.error('Failed to flush session before sending code:', flushError);
    }

    try {
      await sendPhoneVerificationCode(sessionId);
      setCodeSent(true);
      startResendTimer();
    } catch (error) {
      if (error.response?.status === 429) {
        // The throttle answers {detail} only - no message/error key - so the widget's
        // own copy stays the right string for this one branch.
        setError('Please wait before requesting another code.');
      } else {
        // CIN-6961: render the server's cause verbatim, exactly as handleVerifyCode
        // does below and as CIN-6620 established for the /book 409. The widget never
        // maps a code to its own copy and never rewords the server.
        setError(
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to send verification code. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await verifyPhoneCode(sessionId, verificationCode);
      setPhoneVerified(true);
    } catch (error) {
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Invalid code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
    setError('');
  };

  return (
    <div className="phone-verification">
      {!codeSent ? (
        <div className="verification-start">
          <p className="verification-message">
            We need to verify your phone number: <strong>{personalInfo.phone}</strong>
          </p>
          <button
            type="button"
            className="verify-button"
            onClick={handleSendCode}
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Verification Code'}
          </button>
        </div>
      ) : (
        <div className="verification-code-input">
          <p className="verification-message">
            Enter the 6-digit code sent to <strong>{personalInfo.phone}</strong>
          </p>
          <div className="code-input-container">
            <input
              type="text"
              className="code-input"
              value={verificationCode}
              onChange={handleCodeChange}
              placeholder="000000"
              maxLength="6"
              disabled={loading}
            />
            <button
              type="button"
              className="verify-button"
              onClick={handleVerifyCode}
              disabled={loading || verificationCode.length !== 6}
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </div>
          <div className="resend-container">
            {resendTimer > 0 ? (
              <span className="resend-timer">Resend code in {resendTimer}s</span>
            ) : (
              <button
                type="button"
                className="resend-link"
                onClick={handleSendCode}
                disabled={loading}
              >
                Resend code
              </button>
            )}
          </div>
        </div>
      )}
      {/*
        CIN-6961: this node is now the screen's primary diagnostic channel - it
        carries the server's per-cause sentence, not one fixed generic string - so
        it has to be announced, not just rendered. Mounting a role="alert" node is
        the pattern already used for the same job in LeadSalvageModal.js, and the
        announcement fires on insertion; the node stays conditionally mounted
        because .error-message carries margin-top plus a line box, so keeping an
        empty one in the tree would shift the happy-path layout. aria-live is
        stated explicitly (matching SuccessScreen.js) even though role="alert"
        implies assertive, for AT that honours the property but not the mapping.
      */}
      {error && (
        <p
          className="error-message"
          role="alert"
          aria-live="assertive"
          data-testid="verification-error"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default PhoneVerification;